import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import InviteModal from './InviteModal';

const Dashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalMembers: 0,
        pendingInvites: 0,
        activeArtisans: 0,
        admins: 0
    });
    const [recentInvites, setRecentInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await api.get('/api/admin/users');
            if (response.ok) {
                const users = await response.json();

                const stats = {
                    totalMembers: users.length,
                    pendingInvites: users.filter(u => u.status === 'PENDING_INVITE').length,
                    activeArtisans: users.filter(u => u.role === 'ARTISAN' && u.status === 'ACTIVE').length,
                    admins: users.filter(u => u.role === 'ADMIN').length
                };

                const recent = users
                    .filter(u => u.status === 'PENDING_INVITE')
                    .slice(0, 5);

                setStats(stats);
                setRecentInvites(recent);
            }
        } catch (err) {
            console.error('Failed to fetch dashboard data', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-spinner">
                <div className="spinner"></div>
                <p className="loading-text">Loading dashboard...</p>
            </div>
        );
    }

    return (
        <>
            <div className="animate-fadeIn">
                <div className="dashboard-header">
                    <h1>Dashboard</h1>
                    <p>Welcome back! Here's what's happening with your team.</p>
                </div>

                <div className="stat-grid">
                    <div className="stat-card green">
                        <div className="stat-card-content">
                            <div className="stat-card-header">
                                <span className="stat-card-icon">👥</span>
                                <div className="stat-card-value">{stats.totalMembers}</div>
                            </div>
                            <h3 className="stat-card-title">Total Members</h3>
                        </div>
                        <div className="stat-card-bg-icon">👥</div>
                    </div>

                    <div className="stat-card gold">
                        <div className="stat-card-content">
                            <div className="stat-card-header">
                                <span className="stat-card-icon">📧</span>
                                <div className="stat-card-value">{stats.pendingInvites}</div>
                            </div>
                            <h3 className="stat-card-title">Pending Invites</h3>
                        </div>
                        <div className="stat-card-bg-icon">📧</div>
                    </div>

                    <div className="stat-card teal">
                        <div className="stat-card-content">
                            <div className="stat-card-header">
                                <span className="stat-card-icon">✨</span>
                                <div className="stat-card-value">{stats.activeArtisans}</div>
                            </div>
                            <h3 className="stat-card-title">Active Artisans</h3>
                        </div>
                        <div className="stat-card-bg-icon">✨</div>
                    </div>

                    <div className="stat-card purple">
                        <div className="stat-card-content">
                            <div className="stat-card-header">
                                <span className="stat-card-icon">👑</span>
                                <div className="stat-card-value">{stats.admins}</div>
                            </div>
                            <h3 className="stat-card-title">Administrators</h3>
                        </div>
                        <div className="stat-card-bg-icon">👑</div>
                    </div>
                </div>

                <div className="admin-card">
                    <h2>Quick Actions</h2>
                    <div className="quick-actions">
                        <button onClick={() => navigate('/admin/team')} className="quick-action-btn">
                            <span>👥</span>
                            View Team
                        </button>
                        <button onClick={() => setShowInviteModal(true)} className="quick-action-btn secondary">
                            <span>➕</span>
                            Invite Member
                        </button>
                    </div>
                </div>

                {recentInvites.length > 0 ? (
                    <div className="admin-card">
                        <div className="pending-list">
                            <h2>Pending Invitations</h2>
                            {recentInvites.map(user => (
                                <div key={user.id} className="pending-item">
                                    <div className="pending-user">
                                        <div className="user-avatar" style={{ background: 'linear-gradient(135deg, #b48a2b 0%, #d4a84f 100%)' }}>
                                            {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="user-details">
                                            <div className="user-name">{user.name}</div>
                                            <div className="user-email">{user.email}</div>
                                        </div>
                                    </div>
                                    <div className="pending-badges">
                                        <span className="badge badge-pending">
                                            <span className="pulse-dot">●</span>
                                            PENDING
                                        </span>
                                        <span className="role-label">{user.role}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="admin-card">
                        <div className="empty-state">
                            <div className="empty-icon">🎉</div>
                            <h3 className="empty-title">All Caught Up!</h3>
                            <p className="empty-description">No pending invitations at the moment.</p>
                            <button onClick={() => navigate('/admin/team')} className="quick-action-btn">
                                Invite New Member
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {
                showInviteModal && (
                    <InviteModal
                        isOpen={showInviteModal}
                        onClose={() => setShowInviteModal(false)}
                        onSuccess={(email, inviteLink) => {
                            fetchDashboardData();
                        }}
                    />
                )
            }
        </>
    );
};

export default Dashboard;
